// @ts-strict-ignore
import { detectTimezoneName, detectTimezoneOffset, timezones } from 'services/timeKeeping'
import { isNumber } from 'services/numbers'
import { useFragment } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import { useIsGuest, useIsMasked } from 'hooks/user'
import { useDebouncedEffect } from 'hooks/debounce'
import { useIsE2ETest } from 'hooks/utility'
import { useLocalAutocomplete } from 'hooks/search'
import { trackError } from 'services/analytics'
import { commitMutation } from 'api/graphql'
import { timezone_useTimezoneDetectorUser$key } from '__generated__/timezone_useTimezoneDetectorUser.graphql'
import { timezone_UpdateUserTimezoneMutation } from '__generated__/timezone_UpdateUserTimezoneMutation.graphql'
import { useCallback } from 'react'

export function useTimezoneAutocomplete(useOffsetAsValue: boolean = false) {
  return useLocalAutocomplete(useCallback((search: string) => {
    const cleanedSearch = search ? search.replaceAll(/[0+\s]/g, '').toLocaleLowerCase() : ''
    return timezones.filter(tz => tz?.title ? (
      tz.title.toLocaleLowerCase().replaceAll(/[0+\s]/g, '').includes(cleanedSearch)
    ) : false)
      .map(tz => ({ value: useOffsetAsValue ? tz.value : tz.alias, label: tz.title }))
  }, [useOffsetAsValue]))
}

export function useTimezoneDetector(userFragmentRef: timezone_useTimezoneDetectorUser$key) {
  const user = useFragment(graphql`
    fragment timezone_useTimezoneDetectorUser on User {
      timezoneName
      timezoneOffset

      ...user_useIsGuest
      ...user_useIsMasked
    }
  `, userFragmentRef)
  const isGuest = useIsGuest(user)
  const isMasked = useIsMasked(user)
  const isE2ETest = useIsE2ETest()

  useDebouncedEffect(() => {
    (async () => {
      // This behavior messes with Cypress test data, so we disable it in cypress
      // This behavior must be disabled when masked into another user.
      if (user && !isGuest && !isE2ETest && !isMasked) {
        const input = {
          timezoneOffset: detectTimezoneOffset(),
          timezoneName: detectTimezoneName(),
        }
        const valuesValid = isNumber(input.timezoneOffset) && input.timezoneName
        const valuesDifferent = user.timezoneOffset !== input.timezoneOffset && user.timezoneName !== input.timezoneName

        if (valuesValid && valuesDifferent) {
          try {
            await commitMutation<timezone_UpdateUserTimezoneMutation>({
              mutation: graphql`
                mutation timezone_UpdateUserTimezoneMutation($input: UpdateUserInput!) {
                  updateUser(input: $input) {
                    user {
                      timezoneName
                      timezoneOffset
                    }
                  }
                }
              `,
              variables: {
                input,
              },
            })
          } catch (error) {
            trackError(error)
          }
        }
      }
    })()
  }, 30000, [isGuest, isE2ETest, isMasked, user])
}
